import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../core/components/Modal.tsx';
import { camelCase, snakeCase, startCase } from 'lodash';
import { Alert, AlertContent, AlertTitle } from '../../core/components/Alert.tsx';
import { NavigationContext } from '../../document/routes.tsx';
import { useQuery } from '@apollo/client';
import { SEARCH_MASTERDATA_ENTITY_FIELD } from '../queries.ts';

import Table, { RowControlIconButton, TableScrollWrapper } from '../../core/components/Table.tsx';
import { useDebounce } from '../../core/utils/hooks/useDebounce.ts';
import StringField from '../../core/components/Fields/StringField.tsx';
import {
    faChevronDown,
    faChevronRight,
    faExternalLink,
    faMagnifyingGlass,
    faPenField,
    faSidebarFlip,
    faSpinnerThird,
    faWindowRestore,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { url } from '../../core/utils/link.ts';
import Button from '../../core/components/Button.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import AssistanceSection from '../../document/pages/Assistance/AssistanceSection.tsx';

export const RESULTS_PAGE_SIZE = 20;
export const EXCLUDED_MASTERDATA_COLUMNS = new Set([
    'id',
    'versionId',
    'lookupDefinitionId',
    'contentSummaryMultiline',
    '__typename',
    'variants',
    'salesOrganisationOptions',
]);
export const getTextForValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};
export const isNonEmpty = (row: any, key: string) => row[key] !== null && row[key] !== undefined && row[key] !== '';
export const MAX_SKELETON_HEIGHT = 44;
export const MIN_SKELETON_HEIGHT = 5;

const SelectIcon = withIcon(faPenField);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);
const SidebarIcon = withIcon(faSidebarFlip);
const CloseIcon = withIcon(faXmark);
const ModalIcon = withIcon(faWindowRestore);
const SearchIcon = withIcon(faMagnifyingGlass);
const ChevronRightIcon = withIcon(faChevronRight);
const ChevronDownIcon = withIcon(faChevronDown);

const getExtraDataEntries = (value: any): [string, string][] | null => {
    let parsed = value;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return null;
        }
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const entries = Object.entries(parsed);
    if (entries.length === 0) return null;
    return entries.map(([key, val]) => [key, getTextForValue(val)]);
};

export const ExtraDataCell = ({ value, className }: { value: any; className?: string }) => {
    const [expanded, setExpanded] = useState(false);
    const text = getTextForValue(value);

    if (!text) {
        return <Table.Cell className={className} />;
    }

    const entries = getExtraDataEntries(value);
    const ChevronIcon = expanded ? ChevronDownIcon : ChevronRightIcon;

    return (
        <Table.Cell className={classnames('align-top', className)}>
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                className="flex max-w-sm cursor-pointer items-start gap-1 text-left"
            >
                <ChevronIcon className="mt-0.5 h-3 w-3 shrink-0" />
                {!expanded ? (
                    <span className="min-w-0 truncate">{text}</span>
                ) : entries ? (
                    <span className="grid min-w-0 grid-cols-[auto_1fr] gap-x-2 gap-y-1">
                        {entries.map(([key, val]) => (
                            <React.Fragment key={key}>
                                <span className="whitespace-nowrap font-bold text-content-medium">{key}</span>
                                <span className="min-w-0 break-words">{val}</span>
                            </React.Fragment>
                        ))}
                    </span>
                ) : (
                    // Expanded fallback for values that aren't a flat object.
                    <span className="min-w-0 whitespace-pre-wrap break-words">{text}</span>
                )}
            </button>
        </Table.Cell>
    );
};

const HighlightedText = ({ text, pattern }) => {
    if (pattern == null || text == null) {
        return text;
    }
    const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedPattern})`, 'gi'));
    return parts.map((part, index) => (
        <React.Fragment key={index}>
            {part.toLowerCase() === pattern.toLowerCase() ? (
                <b style={{ fontWeight: 700, background: 'rgba(0,0,0,0.05)' }}>{part}</b>
            ) : (
                part
            )}
        </React.Fragment>
    ));
};

const MasterDataBrowserRow = ({
    entry,
    searchTerm,
    targetColumn,
    columns,
    onSelect,
    ...props
}: {
    entry: any;
    searchTerm?: string;
    targetColumn?: string;
    columns: string[];
    onSelect?: (entry: any) => void;
} & React.ComponentPropsWithRef<typeof Table.Row>) => {
    return (
        <Table.Row {...props}>
            {targetColumn && (
                <Table.Cell sticky="left">
                    <HighlightedText text={entry[targetColumn]} pattern={searchTerm} />
                </Table.Cell>
            )}

            {columns
                .filter((key) => key !== targetColumn)
                .map((key) =>
                    key === 'extraData' ? (
                        <ExtraDataCell key={key} value={entry[key]} />
                    ) : (
                        <Table.Cell key={key}>{getTextForValue(entry[key])}</Table.Cell>
                    )
                )}

            <Table.Cell sticky="right" className="left-16">
                <RowControlIconButton onClick={() => onSelect(entry)} disabled={!onSelect}>
                    <SelectIcon />
                </RowControlIconButton>
            </Table.Cell>
        </Table.Row>
    );
};

export const MasterDataLoadingSkeleton = ({ className, lines = 3, ...props }: any) => {
    return (
        <div className={classnames('flex gap-3 animate-pulse', className)} {...props}>
            <div className={classnames('flex gap-2 flex-1')}>
                {[...Array(lines)].map((_, i) => (
                    <div key={i} className="rounded-md h-8 w-full bg-surface-strong opacity-80" />
                ))}
            </div>
        </div>
    );
};

// Lookup types whose results are grouped by a primary key column (e.g. offer/order number),
// so that header & select button only render on the first row of each group and rows collapse visually.
const GROUPED_LOOKUP_GROUPING_COLUMN: Record<string, string> = {
    OfferMatchingLookupType: 'offerNumber',
    OrderMatchingLookupType: 'orderNumber',
};

// Row component for grouped lookup types (offer/order matching) that only shows the grouping
// column and select button on the first row of each group
const GroupedMasterDataBrowserRow = ({
    entry,
    searchTerm,
    columns,
    onSelect,
    isFirstRowForGroup,
    groupingColumn,
    ...props
}: {
    entry: any;
    searchTerm?: string;
    columns: string[];
    onSelect?: (entry: any) => void;
    isFirstRowForGroup: boolean;
    groupingColumn: string;
} & React.ComponentPropsWithRef<typeof Table.Row>) => {
    return (
        <Table.Row {...props} className={classnames(!isFirstRowForGroup && 'border-t-0', props.className)}>
            {/* Grouping column - only show on first row for this group; sticky vertically (top-11) so it stays visible while scrolling through line items, but flows with horizontal scroll */}
            <Table.Cell
                className={classnames(
                    isFirstRowForGroup ? 'sticky top-11 border-t border-b-0' : 'border-t-0 border-b-0'
                )}
            >
                {isFirstRowForGroup ? <HighlightedText text={entry[groupingColumn]} pattern={searchTerm} /> : null}
            </Table.Cell>

            {/* Other columns - always show */}
            {columns
                .filter((key) => key !== groupingColumn)
                .map((key) =>
                    key === 'extraData' ? (
                        <ExtraDataCell
                            key={key}
                            value={entry[key]}
                            className={isFirstRowForGroup ? 'border-t border-b-0' : 'border-t-0 border-b-0'}
                        />
                    ) : (
                        <Table.Cell
                            key={key}
                            className={isFirstRowForGroup ? 'border-t border-b-0' : 'border-t-0 border-b-0'}
                        >
                            {getTextForValue(entry[key])}
                        </Table.Cell>
                    )
                )}

            {/* Select button - only show on first row for this group; sticky vertically (top-11) so it stays visible while scrolling through line items, but flows with horizontal scroll */}
            <Table.Cell
                className={classnames(
                    isFirstRowForGroup ? 'sticky top-11 border-t border-b-0' : 'border-t-0 border-b-0'
                )}
            >
                {isFirstRowForGroup ? (
                    <RowControlIconButton onClick={() => onSelect?.(entry)} disabled={!onSelect}>
                        <SelectIcon />
                    </RowControlIconButton>
                ) : null}
            </Table.Cell>
        </Table.Row>
    );
};

export const MasterDataBrowser = ({
    searchTerm: initialSearchTerm,
    lookupType,
    targetField,
    lookupDefinitionId,
    partitionId,
    extraSearchTerms,
    dropdownMessageBuilder,
    searchMessageBuilder,
    onSelectRecord,
    extractionSearchResults = null,
}: any) => {
    const { t } = useTranslation('masterdata');
    const { paths, channelId } = useContext(NavigationContext);

    const [activeSearchTerm, setActiveSearchTerm] = useState(initialSearchTerm);
    const setActiveSearchTermDebounced = useDebounce(setActiveSearchTerm, 500);
    const [searchInputText, setSearchInputText] = useState(initialSearchTerm);
    const [rows, setRows] = useState([]);
    const scrollRef = useRef(null);
    const skeletonContainerRef = useRef(null);
    const [skeletonCount, setSkeletonCount] = useState(12);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(!!lookupDefinitionId);
    const [batchesLoaded, setBatchesLoaded] = useState(0);

    useEffect(() => {
        // Synchronize state with external prop, i.e. re-initialize when search term prop changes
        setSearchInputText(initialSearchTerm);
        setActiveSearchTerm(initialSearchTerm);
    }, [initialSearchTerm]);

    const { loading, fetchMore } = useQuery(SEARCH_MASTERDATA_ENTITY_FIELD, {
        variables: {
            lookupDefinitionId,
            targetField,
            searchTerm: activeSearchTerm,
            partitionId: partitionId || null,
            from: 0,
            size: RESULTS_PAGE_SIZE,
            kwargs: extraSearchTerms,
        },
        fetchPolicy: 'cache-and-network',
        skip: !lookupDefinitionId,
        onCompleted: (data) => {
            const newRows = data?.assistanceSearchByField?.edges?.map((edge) => edge.node) || [];
            setRows(newRows);

            if (newRows.length < RESULTS_PAGE_SIZE) {
                setHasMore(false);
            }
            setBatchesLoaded(1);
            setIsSearching(false);
        },
        onError: () => {
            setIsSearching(false);
        },
    });

    useEffect(() => {
        // reset when search term changes
        setRows([]);
        setHasMore(true);
        setIsSearching(true);
        setBatchesLoaded(0);
        scrollRef?.current?.scrollTo({ top: 0 });
    }, [activeSearchTerm]);

    useEffect(() => {
        const calculateSkeletonCount = () => {
            if (skeletonContainerRef.current) {
                const containerHeight = skeletonContainerRef.current.clientHeight;
                const skeletonRowHeight = MAX_SKELETON_HEIGHT;
                const calculatedCount = Math.floor(containerHeight / skeletonRowHeight);
                setSkeletonCount(Math.max(calculatedCount, MIN_SKELETON_HEIGHT));
            }
        };

        calculateSkeletonCount();
        window.addEventListener('resize', calculateSkeletonCount);
        return () => window.removeEventListener('resize', calculateSkeletonCount);
    }, [loading, isSearching]);

    const isAtBottom = ({ currentTarget }) =>
        currentTarget.scrollTop + 10 >= currentTarget.scrollHeight - currentTarget.clientHeight;

    const handleScroll = async (event) => {
        if (isLoading || !hasMore || loading) return;

        const atBottom = isAtBottom(event);
        if (!atBottom) return;

        setIsLoading(true);

        try {
            await fetchMore({
                variables: {
                    from: rows.length,
                    size: RESULTS_PAGE_SIZE,
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;

                    const newEdges = fetchMoreResult.assistanceSearchByField?.edges || [];
                    const newRows = newEdges.map((edge) => edge.node);

                    setRows((prevRows) => [...prevRows, ...newRows]);
                    setBatchesLoaded((prev) => prev + 1);

                    if (newEdges.length < RESULTS_PAGE_SIZE) {
                        setHasMore(false);
                    }

                    return {
                        assistanceSearchByField: {
                            ...fetchMoreResult.assistanceSearchByField,
                            edges: [...(prev.assistanceSearchByField?.edges || []), ...newEdges],
                        },
                    };
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const dropdownMessage = !loading && !isSearching ? dropdownMessageBuilder?.(t, activeSearchTerm, rows) : null;
    const searchMessage =
        activeSearchTerm && !loading && !isSearching && searchMessageBuilder?.(t, activeSearchTerm, rows);

    const baseColumns =
        rows.length > 0
            ? Object.keys(rows[0])
                  .filter((key) => !EXCLUDED_MASTERDATA_COLUMNS.has(key) && key !== 'extraData')
                  .filter((key) => rows.some((row) => isNonEmpty(row, key)))
            : [];
    const hasExtraData = rows.some((row) => isNonEmpty(row, 'extraData'));
    const columns = hasExtraData ? [...baseColumns, 'extraData'] : baseColumns;
    const targetColumn = columns.includes(camelCase(targetField)) ? camelCase(targetField) : undefined;
    const isSidePanelView = localStorage.getItem('masterdataViewMode') === 'sidepanel';

    const groupingColumn = GROUPED_LOOKUP_GROUPING_COLUMN[lookupType];
    const isGroupedLookup = !!groupingColumn;
    // Must match the column each row body hoists into its first cell (grouping vs target).
    const leadingColumn = isGroupedLookup ? groupingColumn : targetColumn;

    // Track which group keys we've seen to show the grouping column and select button only on the first row of each group
    const getSeenGroupKeys = () => {
        const seen = new Set<string>();
        return (key: string) => {
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        };
    };

    return (
        <div className="flex flex-col !h-[calc(100dvh-7rem)] gap-6 pb-6 overflow-hidden">
            <div
                className={classnames(
                    'flex gap-4 justify-between items-center',
                    isSidePanelView ? 'flex-col items-start' : ''
                )}
            >
                <SearchIcon
                    className={classnames(
                        'pointer-events-none absolute top-auto ml-2 pt-2 h-3 w-3 text-content-weak',
                        !isSidePanelView ? 'pt-0' : ''
                    )}
                />
                <StringField
                    placeholder={t('browser.searchInputPlaceholder')}
                    value={searchInputText}
                    onValueChange={(value) => {
                        setSearchInputText(value);
                        setActiveSearchTermDebounced(value);
                    }}
                    className={classnames(!isSidePanelView ? 'max-w-[20rem]' : '', 'pl-4')}
                    controls={
                        searchInputText && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchInputText('');
                                    setActiveSearchTermDebounced('');
                                }}
                                className="p-1 hover:bg-surface-weak rounded cursor-pointer"
                                aria-label="Clear search"
                            >
                                <CloseIcon className="w-3 h-3 text-content-medium" />
                            </button>
                        )
                    }
                />
                {!dropdownMessage && (
                    <span className="text-content-medium text-sm font-normal">{searchMessage || ''}</span>
                )}
            </div>
            {(loading || isSearching) && rows.length === 0 && !dropdownMessage && (
                <div ref={skeletonContainerRef} className="flex flex-col gap-3 flex-1 overflow-hidden">
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <MasterDataLoadingSkeleton key={index} />
                    ))}
                </div>
            )}
            {extractionSearchResults?.results?.length > 0 && searchInputText === initialSearchTerm && (
                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-content-weak">Top matches based on document</span>
                    <Table>
                        <Table.Body>
                            {extractionSearchResults.results.map((result: any, index: number) => {
                                const selectResult = () =>
                                    onSelectRecord?.({
                                        [camelCase(targetField)]: result.description,
                                        articleId1: result.articleId,
                                        description: result.description,
                                        // Marks the commit payload (`_lookupEntity`) as an extraction search
                                        // selection, so the backend resolves the article by its number and
                                        // fills the line item's master data (stored results carry no
                                        // masterdata entity id, i.e. no `_lookup_id`)
                                        _extractionSearchResult: true,
                                    });
                                return (
                                    <Table.Row
                                        key={`extraction-${index}`}
                                        className="cursor-pointer hover:bg-surface-weak"
                                        onClick={selectResult}
                                    >
                                        <Table.Cell className="font-medium">{result.articleId}</Table.Cell>
                                        <Table.Cell>{result.description}</Table.Cell>
                                        <Table.Cell className="text-content-weak text-right w-16">
                                            <RowControlIconButton
                                                icon={SelectIcon}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    selectResult();
                                                }}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </div>
            )}
            {!dropdownMessage && rows.length > 0 && (
                <>
                    <TableScrollWrapper onScroll={handleScroll} ref={scrollRef}>
                        <Table>
                            <Table.Head sticky>
                                <Table.Row>
                                    {leadingColumn && (
                                        <Table.HeadCell>
                                            {t(`schema.${lookupType}.${leadingColumn}`, {
                                                defaultValue: startCase(leadingColumn),
                                            })}
                                        </Table.HeadCell>
                                    )}
                                    {columns
                                        .filter((key) => key !== leadingColumn)
                                        .map((key) => (
                                            <Table.HeadCell
                                                key={key}
                                                className={classnames(
                                                    key === 'description' || key === 'articleDescription'
                                                        ? 'min-w-[20rem]'
                                                        : ''
                                                )}
                                                data-key={key}
                                            >
                                                {t(`schema.${lookupType}.${key}`, { defaultValue: startCase(key) })}
                                            </Table.HeadCell>
                                        ))}
                                    <Table.HeadCell />
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {isGroupedLookup
                                    ? (() => {
                                          const isFirstForGroup = getSeenGroupKeys();
                                          return rows.map((row, index) => (
                                              <GroupedMasterDataBrowserRow
                                                  key={row.id || index}
                                                  entry={row}
                                                  searchTerm={activeSearchTerm}
                                                  columns={columns}
                                                  onSelect={onSelectRecord}
                                                  isFirstRowForGroup={isFirstForGroup(row[groupingColumn])}
                                                  groupingColumn={groupingColumn}
                                              />
                                          ));
                                      })()
                                    : rows.map((row, index) => (
                                          <MasterDataBrowserRow
                                              key={row.id || index}
                                              entry={row}
                                              searchTerm={activeSearchTerm}
                                              targetColumn={targetColumn}
                                              columns={columns}
                                              onSelect={onSelectRecord}
                                          />
                                      ))}
                            </Table.Body>
                        </Table>
                    </TableScrollWrapper>
                    {(loading || isLoading || hasMore) && (
                        <div className="-mt-[65px] z-9 left-0 w-full text-center py-2 pointer-events-none">
                            <span className="rounded-[100px] pr-4 pl-4 py-[6px] bg-surface-black text-content-white text-sm font-medium">
                                {(loading || isLoading) && (
                                    <LoadingIcon className="text-[#28A199] text-sm animate-spin w-[2rem] h-[2.5rem] -ml-[8px]" />
                                )}
                                {loading || isLoading ? t('browser.loading') : t('browser.scroll')}
                            </span>
                        </div>
                    )}
                </>
            )}
            {!!dropdownMessage && (
                <Alert className="masterdata-browser__content--info !h-[calc(100dvh-7rem)] flex flex-col justify-center text-center">
                    <span>{dropdownMessage}</span>
                </Alert>
            )}
            {(batchesLoaded >= 3 || !hasMore) && (
                <Alert className="masterdata-browser__content--disclaimer bg-[#EEF2FF] text-indigo-900">
                    <div className={classnames('flex justify-between flex-row', isSidePanelView ? 'flex-col' : '')}>
                        <div>
                            <AlertTitle>{t('browser.disclaimer.title')}</AlertTitle>
                            <AlertContent>
                                <span>{t('browser.disclaimer.description')}</span>
                            </AlertContent>
                        </div>
                        <a
                            className={classnames(
                                'self-center bg-white p-2 pt-1 h-8 rounded-sm border-1 border-indigo-200',
                                isSidePanelView ? 'self-start mt-2' : ''
                            )}
                            href={url(paths.channelMasterdata, { channelId })}
                            target="_blank"
                        >
                            <ExternalLinkIcon className="pr-2" />
                            {t('browser.disclaimer.linkText')}
                        </a>
                    </div>
                </Alert>
            )}
        </div>
    );
};

export const MasterDataBrowserModal = ({ visible, onClose, onSwitchToSidePanel, ...browserProps }: any) => {
    const { t } = useTranslation('masterdata');

    const lookupTypeLocalizationKey = snakeCase(browserProps.lookupType?.replace('Type', '') || '').toUpperCase(); // e.g. "ARTICLE_LOOKUP"
    return (
        <Modal
            className="xl:min-w-[72rem]"
            visible={visible}
            onClose={onClose}
            title={t('browser.title', { lookupType: t(`importRun.lookupType.${lookupTypeLocalizationKey}`) })}
            headerButtons={[
                <Button
                    className="h-8 text-content-default pb-2 py-1"
                    variant="ghost"
                    key="side-panel"
                    onClick={onSwitchToSidePanel}
                >
                    <SidebarIcon className="text-content-default w-[16px] h-[14px] pr-1"></SidebarIcon>
                    <span className="text-medium font-medium">{t('browser.switchToSidePanel')}</span>
                </Button>,
            ]}
        >
            <MasterDataBrowser {...browserProps} />
        </Modal>
    );
};

export const MasterDataBrowserSidePanel = ({ visible, onClose, onSwitchToModal, ...browserProps }: any) => {
    const { t } = useTranslation('masterdata');
    const [hasScroll, setHasScroll] = useState(false);
    const contentContainerRef = useRef(null);

    const lookupTypeLocalizationKey = snakeCase(browserProps.lookupType?.replace('Type', '') || '').toUpperCase(); // e.g. "ARTICLE_LOOKUP"

    useEffect(() => {
        const el = contentContainerRef.current;
        if (el) {
            const checkScroll = () => {
                const scroll = el.scrollHeight > el.clientHeight;
                setHasScroll(scroll);
            };

            checkScroll();

            const resizeObserver = new ResizeObserver(checkScroll);
            resizeObserver.observe(el);

            return () => resizeObserver.disconnect();
        }
    }, [browserProps]);

    if (!visible) return null;

    return (
        <div className="h-full w-full bg-surface-weak flex flex-col">
            <div className="flex justify-between items-center gap-8 px-6 border-b border-solid border-line-weak flex-none min-h-[3.5rem]">
                <div className="text-base font-semibold text-content-strong max-w-[32rem]">
                    {t('browser.title', { lookupType: t(`importRun.lookupType.${lookupTypeLocalizationKey}`) })}
                </div>

                <div className="flex">
                    <Tooltip content={t('browser.switchToFullScreen')} side="left">
                        <Button
                            className="text-content-default pb-2 cursor-pointer"
                            variant="ghost"
                            key="modal"
                            onClick={onSwitchToModal}
                        >
                            <ModalIcon className="text-content-default w-[14px] h-[14px] pr-1"></ModalIcon>
                        </Button>
                    </Tooltip>

                    <div className="w-8 h-8">
                        <Button
                            variant="ghost"
                            tabIndex={-1}
                            className="w-8 h-8 -mr-4 p-0 flex items-center cursor-pointer justify-center text-content-strong"
                            onClick={onClose}
                        >
                            <CloseIcon className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </div>

            <div
                ref={contentContainerRef}
                className={classnames(
                    'w-full flex flex-col gap-2 overflow-auto flex-1 px-6 pt-6 text-base text-content-strong',
                    hasScroll && 'border-b border-line-weak pb-6'
                )}
            >
                <MasterDataBrowser {...browserProps} />
            </div>
        </div>
    );
};
